import type { WorkbookDefinition } from '@erp/shared-types';
import {
  definitionWithFormulaText,
  definitionWithStoredFormulas,
  describeRunModeBlocker,
  runModeOption,
  type RunModeBlocker,
} from '@erp/workbooks';
import { rpcClient } from '@/api/rpc/client';
import type { WorkbookAgentPort } from './useWorkbookAgentTools';
import {
  applyWorkbookAgentOperations,
  type WorkbookAgentOperation,
} from './workbookAgentEdits';
import { agentPreviewResults } from './workbookAgentReads';

type AgentPreviewRequest = {
  /** The target Worksheet's id or name. */
  sheet: string;
  operations: readonly WorkbookAgentOperation[];
  rowLimit: number;
};

/** The Worksheet an agent names, by id, else by its name (ignoring case) when exactly one has it. */
export function targetSheet(definition: WorkbookDefinition, sheet: string) {
  const byId = definition.sheets.find((entry) => entry.id === sheet);
  if (byId) return byId;
  const named = definition.sheets.filter(
    (entry) => entry.name.toLowerCase() === sheet.trim().toLowerCase(),
  );
  if (named.length === 1) return named[0];
  throw new Error(
    named.length
      ? `Several worksheets are named '${sheet}': pass the worksheet's id.`
      : `No worksheet has the id or name '${sheet}' in the edited workbook.`,
  );
}

/** Why a preview cannot start, for the agent to act on. */
function blockerMessage(
  definition: WorkbookDefinition,
  blocker: RunModeBlocker,
  sheet: { id: string; name: string },
): string {
  switch (blocker.code) {
    case 'diagnostic':
      return `The edited workbook cannot be previewed from '${sheet.name}': ${describeRunModeBlocker(definition, blocker)} Fix the diagnostics below in your edit and preview again.`;
    case 'no-base-run':
    case 'not-in-base-run':
    case 'changed-since-base-run':
      return `No Base run to preview '${sheet.name}' on: a preview reuses a completed run's inputs, and ${describeRunModeBlocker(definition, blocker)} Run through '${sheet.name}' first (run_open_workbook, mode 'up-to', targetId ${sheet.id}; ask the user before running), then preview again.`;
    default:
      return describeRunModeBlocker(definition, blocker);
  }
}

function preview(
  workbookId: string,
  definition: WorkbookDefinition,
  targetSheetId: string,
) {
  // The agent's previews queue apart from the page's own, so neither replaces the other.
  return rpcClient.workbooks.preview({
    workbookId,
    definition,
    targetSheetId,
    requester: 'agent',
  });
}

/**
 * Tries an agent edit without committing it: applies `operations` to the
 * latest Definition in memory, plans it, and computes a Live preview of it
 * from the target Worksheet on the Base run's Snapshot. Nothing is applied to
 * the editor, saved or persisted. Returns a failure the agent can act on when
 * the edit is refused, the Plan blocks the preview, or there is no Base run.
 */
export async function previewAgentChange(
  workbookId: string,
  port: WorkbookAgentPort,
  request: AgentPreviewRequest,
) {
  const { definition: current } = port.snapshot();
  const columns = port.plan(current).tables;
  const edited = request.operations.length
    ? definitionWithStoredFormulas(
        applyWorkbookAgentOperations(
          definitionWithFormulaText(current, { columns }),
          request.operations,
        ),
        { columns, base: current },
      )
    : current;
  const sheet = targetSheet(edited, request.sheet);
  const plan = port.plan(edited);
  const option = runModeOption(
    {
      definition: edited,
      diagnostics: plan.diagnostics,
      runs: await rpcClient.workbooks.runs({ workbookId }),
    },
    'from',
    sheet.id,
  );
  if (option.blocker)
    return {
      ok: false as const,
      error: blockerMessage(edited, option.blocker, sheet),
      diagnostics: plan.diagnostics,
    };
  const response = await preview(workbookId, edited, sheet.id);
  return {
    committed: false,
    target: { sheetId: sheet.id, worksheet: sheet.name },
    baseRunId: response.baseRunId,
    inputsAsOf: response.inputsAsOf,
    freshSources: response.freshSources,
    diagnostics: plan.diagnostics,
    results: agentPreviewResults(
      edited,
      plan,
      response.results,
      request.rowLimit,
    ),
  };
}
